import { useEffect, useState } from 'react';
import {
  getResignationDocuments,
  uploadResignationDocument,
  downloadResignationDocument,
  type ResignationDocument,
} from '@urbanik/api-client';
import { Button, DocumentCard, FileUpload, InfoPanel, formatDateTime } from '@urbanik/ui';
import { Upload, Download, RefreshCw, Eye, Replace, Trash2 } from 'lucide-react';
import { ResignationDocumentChange } from './ResignationDocumentChange';
import { ResignationDocumentPreview } from './ResignationDocumentPreview';
import { apiBase } from './api-base';
export function ResignationDocuments({
  memberId,
  historyId,
  recordedAt,
}: {
  memberId: string;
  historyId: string;
  recordedAt: string;
}) {
  const [documents, setDocuments] = useState<ResignationDocument[] | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ResignationDocument | null>(null);
  const [editing, setEditing] = useState<{
    document: ResignationDocument;
    mode: 'replace' | 'delete';
  } | null>(null);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false),
    [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    setDocuments(null);
    setFile(null);
    setPreview(null);
    setEditing(null);
    setError('');
    getResignationDocuments(apiBase, memberId, historyId, c.signal)
      .then((r) => setDocuments(r.documents))
      .catch((e) => {
        if (!c.signal.aborted)
          setError(e instanceof Error ? e.message : 'No pudimos consultar las constancias.');
      });
    return () => c.abort();
  }, [memberId, historyId, attempt]);
  const upload = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const r = await uploadResignationDocument(apiBase, memberId, historyId, file);
      setDocuments(r.documents);
      setFile(null);
      setNotice('Constancia archivada. Puedes adjuntar otro PDF.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos adjuntar el archivo.');
    } finally {
      setBusy(false);
    }
  };
  const download = async (d: ResignationDocument) => {
    setBusy(true);
    setError('');
    try {
      const blob = await downloadResignationDocument(apiBase, memberId, historyId, d.id),
        url = URL.createObjectURL(blob),
        a = document.createElement('a');
      a.href = url;
      a.download = d.fileName;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos descargar el documento.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <DocumentCard
      title={`Constancias de renuncia · ${formatDateTime(recordedAt)}`}
      description="Hasta 10 PDF de 20 MB cada uno por registro. Puedes adjuntar, previsualizar, reemplazar o eliminar constancias con motivo y confirmación."
      upload={
        <>
          {editing ? (
            <ResignationDocumentChange
              memberId={memberId}
              historyId={historyId}
              document={editing.document}
              mode={editing.mode}
              onClose={() => setEditing(null)}
              onChanged={(docs) => {
                setDocuments(docs);
                setPreview(null);
                setEditing(null);
                setError('');
                setNotice(
                  editing.mode === 'delete'
                    ? 'Constancia eliminada del almacenamiento activo.'
                    : 'Constancia reemplazada. El archivo anterior fue retirado.',
                );
              }}
            />
          ) : null}
          {preview ? (
            <ResignationDocumentPreview
              key={preview.id}
              memberId={memberId}
              historyId={historyId}
              document={preview}
              onClose={() => setPreview(null)}
            />
          ) : null}
          {error ? <InfoPanel tone="danger">{error}</InfoPanel> : null}
          {notice ? <InfoPanel tone="success">{notice}</InfoPanel> : null}
          {documents === null ? (
            <Button
              type="button"
              icon={<RefreshCw aria-hidden="true" />}
              onClick={() => setAttempt((v) => v + 1)}
              disabled={busy}
            >
              {error ? 'Reintentar' : 'Consultar constancias'}
            </Button>
          ) : (
            <>
              {documents.length === 0 ? (
                <InfoPanel>Sin documentos adjuntos todavía.</InfoPanel>
              ) : (
                documents.map((d) => (
                  <DocumentCard
                    key={d.id}
                    title={d.fileName}
                    description={`${(d.sizeBytes / 1024 / 1024).toFixed(2)} MB · ${formatDateTime(d.uploadedAt)}`}
                    actions={
                      <>
                        <Button
                          type="button"
                          icon={<Eye aria-hidden="true" />}
                          disabled={busy}
                          onClick={() => setPreview(d)}
                        >
                          Previsualizar
                        </Button>
                        <Button
                          type="button"
                          icon={<Download aria-hidden="true" />}
                          disabled={busy}
                          onClick={() => void download(d)}
                        >
                          Descargar
                        </Button>
                        <Button
                          type="button"
                          icon={<Replace aria-hidden="true" />}
                          disabled={busy}
                          onClick={() => setEditing({ document: d, mode: 'replace' })}
                        >
                          Reemplazar
                        </Button>
                        <Button
                          type="button"
                          icon={<Trash2 aria-hidden="true" />}
                          disabled={busy}
                          onClick={() => setEditing({ document: d, mode: 'delete' })}
                        >
                          Eliminar
                        </Button>
                      </>
                    }
                  />
                ))
              )}
              <FileUpload
                label="Añadir constancia de renuncia (PDF)"
                file={file}
                onFileChange={setFile}
                onError={setError}
                disabled={busy || documents.length >= 10}
                helpText="PDF, máximo 20 MB. Se archivará en este registro de renuncia."
              />
              <Button
                type="button"
                variant="primary"
                icon={<Upload aria-hidden="true" />}
                disabled={busy || !file || documents.length >= 10}
                onClick={() => void upload()}
              >
                {busy ? 'Procesando…' : 'Adjuntar constancia'}
              </Button>
            </>
          )}
        </>
      }
    />
  );
}
